'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Flag, X, Loader2, Crosshair, Trash2, Highlighter } from 'lucide-react'
import { useLocale } from 'next-intl'
import { usePathname } from '@/i18n/navigation'

/**
 * Report-an-issue button — a small floating action (rendered only for
 * logged-in users, see FloatingActions) that opens a dialog and files a
 * support ticket via POST /api/support, pre-filled with the current page
 * URL. Tickets land in the same /admin/support queue + email flow as the
 * regular support form.
 *
 * Optional "point at the issue": the user clicks the problem spot on the
 * page; we drop a marker there, capture a viewport screenshot with
 * html-to-image (lazy-loaded), and attach it to the ticket as an image
 * (the /api/support route already accepts image attachments). Elements
 * tagged `data-screenshot-exclude` (the floating buttons, our own overlays)
 * are filtered out of the capture, as are cross-origin <iframe>s (embedded
 * Vimeo/YouTube players) and cross-origin <img>s (protected/CDN media) — both
 * would otherwise throw and abort the whole capture, so skipping them lets the
 * rest of the page capture and still attach a screenshot (it falls back to the
 * text-only report only if capture fails entirely).
 *
 * "Translation problem" category (non-English locales only): the dialog
 * hides while the user highlights the mistranslated text on the page; the
 * highlight + locale ride along with the ticket, and /api/support resolves
 * the ENGLISH source (UI-string catalogs and/or the Sanity body block) so
 * support sees the translated/EN pair — see
 * src/lib/support/resolve-translation-source.ts.
 */
/**
 * True when an <img> renders a bitmap from another origin. Such images taint
 * the capture canvas (see the filter in `onPick`), so they're dropped from the
 * screenshot. `data:` / `blob:` sources are same-origin-safe and never taint,
 * so they count as same-origin. An unparseable or empty source is treated as
 * safe (kept) — there's nothing cross-origin to taint with.
 */
function isCrossOriginImage(img: HTMLImageElement): boolean {
  // currentSrc reflects the source actually chosen (srcset/picture); fall back
  // to the attribute for images the browser hasn't resolved yet.
  const raw = img.currentSrc || img.getAttribute('src') || ''
  if (!raw) return false
  try {
    const url = new URL(raw, window.location.href)
    if (url.protocol === 'data:' || url.protocol === 'blob:') return false
    return url.origin !== window.location.origin
  } catch {
    return false
  }
}

const CATEGORIES = [
  { value: 'technical', label: 'Something is broken / not working' },
  { value: 'content', label: 'Content error (wrong info, typo, image)' },
  { value: 'translation', label: 'Translation problem (wrong or untranslated text)' },
  { value: 'account', label: 'Account / access' },
  { value: 'other', label: 'Something else' },
] as const

type Status = 'idle' | 'loading' | 'success' | 'error'

export function ReportIssueButton() {
  const pathname = usePathname()
  const locale = useLocale()
  const [open, setOpen] = useState(false)
  const [issue, setIssue] = useState('')
  const [category, setCategory] = useState('technical')
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')
  const [shot, setShot] = useState<string | null>(null)
  const [shotInfo, setShotInfo] = useState('')
  const [shotError, setShotError] = useState('')
  const [picking, setPicking] = useState(false)
  const [capturing, setCapturing] = useState(false)
  // Translation report: the text the user highlighted as mistranslated.
  // `highlighting` hides the dialog so they can select text on the page.
  const [transText, setTransText] = useState('')
  const [highlighting, setHighlighting] = useState(false)
  const [liveSelection, setLiveSelection] = useState('')

  // The translation category only makes sense away from the source
  // language — on English pages there is no translation to be wrong.
  const categories = CATEGORIES.filter(
    (c) => c.value !== 'translation' || locale !== 'en'
  )

  // While in highlight mode, mirror the live selection into state so the
  // banner can show what "Use highlighted text" will capture. Only
  // non-empty selections are kept: on touch devices, tapping the banner's
  // button collapses the native selection BEFORE the click lands (mousedown
  // preventDefault fires too late there), so dropping to '' on collapse
  // would disable the button under the member's finger. Keeping the last
  // non-empty selection makes the confirm tap reliable everywhere.
  useEffect(() => {
    if (!highlighting) return
    const onSelect = () => {
      const sel = (window.getSelection()?.toString() ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 600)
      if (sel) setLiveSelection(sel)
    }
    document.addEventListener('selectionchange', onSelect)
    return () => document.removeEventListener('selectionchange', onSelect)
  }, [highlighting])

  // Leaving the page mid-highlight would strand the banner (this component
  // lives in the persistent layout) — exit the mode on client navigation.
  useEffect(() => {
    setHighlighting(false)
  }, [pathname])

  const reset = () => {
    setIssue('')
    setCategory('technical')
    setStatus('idle')
    setMessage('')
    setShot(null)
    setShotInfo('')
    setShotError('')
    setTransText('')
    setLiveSelection('')
    setHighlighting(false)
  }
  const close = () => {
    setOpen(false)
    setTimeout(reset, 150)
  }

  const onPick = async (clientX: number, clientY: number, target: Element | null) => {
    setPicking(false)
    setCapturing(true)
    setShotError('')
    const nearText = (target?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60)
    setShotInfo(
      `near “${nearText || target?.tagName?.toLowerCase() || 'element'}” at x:${Math.round(clientX)}, y:${Math.round(clientY + window.scrollY)}`
    )
    // Drop a marker at the page coordinate so it appears in the capture.
    const marker = document.createElement('div')
    Object.assign(marker.style, {
      position: 'absolute',
      left: `${window.scrollX + clientX}px`,
      top: `${window.scrollY + clientY}px`,
      width: '30px',
      height: '30px',
      marginLeft: '-15px',
      marginTop: '-15px',
      border: '3px solid #ef4444',
      borderRadius: '9999px',
      boxShadow: '0 0 0 4px rgba(239,68,68,0.35)',
      zIndex: '2147483646',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>)
    document.body.appendChild(marker)
    try {
      await new Promise((r) => requestAnimationFrame(() => r(null)))
      const { toJpeg } = await import('html-to-image')
      const dataUrl = await toJpeg(document.body, {
        quality: 0.9,
        pixelRatio: 1,
        cacheBust: true,
        // Web fonts are fetched + inlined by default; a cross-origin font that
        // lacks CORS makes the whole capture throw. A bug screenshot doesn't
        // need pixel-perfect fonts.
        skipFonts: true,
        // If a single image can't be fetched (CORS/404), substitute a blank
        // pixel instead of failing the entire capture.
        imagePlaceholder:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
        style: {
          transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`,
          transformOrigin: 'top left',
        },
        filter: (node) => {
          if (node instanceof Element) {
            if (node.hasAttribute('data-screenshot-exclude')) return false
            // Cross-origin <iframe>/embeds (Vimeo/YouTube players, etc.) throw a
            // SecurityError when html-to-image reads their contents, which kills
            // the whole capture. Skip them — the rest of the page still renders.
            const tag = node.tagName
            if (tag === 'IFRAME' || tag === 'VIDEO' || tag === 'EMBED' || tag === 'OBJECT') {
              return false
            }
            // Cross-origin <img>s (Vimeo thumbnails, protected/CDN media without
            // CORS) taint the canvas when html-to-image draws the cloned DOM,
            // and `canvas.toDataURL()` then throws a SecurityError that fails the
            // ENTIRE capture — the reason this page fell back to text-only. Drop
            // any image whose resolved source is on another origin so the rest of
            // the page still captures. `imagePlaceholder` (above) still covers
            // same-origin images that merely fail to fetch (CORS/404); data: and
            // blob: images never taint, so they're kept.
            if (tag === 'IMG') {
              if (isCrossOriginImage(node as HTMLImageElement)) return false
            }
          }
          return true
        },
      })
      setShot(dataUrl)
    } catch (err) {
      // Capture failed (tainted canvas, blocked resource, …) — surface why and
      // fall back to the marked-location text so the report still helps.
      console.error('[report] screenshot capture failed:', err)
      setShot(null)
      setShotError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not capture a screenshot of this page.'
      )
    } finally {
      marker.remove()
      setCapturing(false)
      setOpen(true)
    }
  }

  // A translation report with highlighted text can submit without a
  // description — the highlight IS the report.
  const hasTranslationHighlight = category === 'translation' && Boolean(transText)
  const canSubmit = Boolean(issue.trim()) || hasTranslationHighlight

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setStatus('loading')
    const fullUrl = typeof window !== 'undefined' ? window.location.href : pathname
    const description = issue.trim() || 'Translation issue (see highlighted text below).'
    const bodyText = `${description}\n\n———\nReported from: ${fullUrl}${shotInfo ? `\nMarked location: ${shotInfo}` : ''}`
    const subject =
      category === 'translation'
        ? `Translation issue report: ${pathname}`
        : `Page issue report: ${pathname}`
    const translation = hasTranslationHighlight
      ? { locale, text: transText, pageUrl: fullUrl }
      : null
    try {
      let res: Response
      if (shot) {
        const blob = await (await fetch(shot)).blob()
        const fd = new FormData()
        fd.append('subject', subject)
        fd.append('body', bodyText)
        fd.append('category', category)
        fd.append('priority', 'normal')
        if (translation) {
          fd.append('translation_locale', translation.locale)
          fd.append('translation_text', translation.text)
          fd.append('translation_page_url', translation.pageUrl)
        }
        fd.append('files', new File([blob], 'page-screenshot.jpg', { type: 'image/jpeg' }))
        res = await fetch('/api/support', { method: 'POST', body: fd })
      } else {
        res = await fetch('/api/support', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subject,
            body: bodyText,
            category,
            priority: 'normal',
            ...(translation ? { translation } : {}),
          }),
        })
      }
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setStatus('success')
        setMessage(
          data.ticketId
            ? `Thanks! Ticket #${data.ticketId} was created — our team will take a look.`
            : 'Thanks! Your report was submitted.'
        )
      } else {
        setStatus('error')
        setMessage(data.error || 'Could not submit your report. Please try again.')
      }
    } catch {
      setStatus('error')
      setMessage('Network error. Please try again.')
    }
  }

  return (
    <>
      <button
        type="button"
        // Exiting highlight mode here matters: without it, opening the
        // dialog from the Flag while a highlight is in progress would mount
        // the dialog's backdrop OVER the banner and strand it.
        onClick={() => {
          setHighlighting(false)
          setOpen(true)
        }}
        aria-label="Report an issue on this page"
        title="Report an issue with this page"
        className="flex h-10 w-10 items-center justify-center bg-brand-700 text-white shadow-lg transition-all duration-300 hover:bg-brand-800 hover:shadow-xl"
      >
        <Flag className="h-5 w-5" />
      </button>

      {/* Point-at-the-issue overlay */}
      {picking &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-screenshot-exclude
            className="fixed inset-0 z-[100] cursor-crosshair"
            onClick={(e) => {
              const overlay = e.currentTarget as HTMLElement
              overlay.style.display = 'none'
              const target = document.elementFromPoint(e.clientX, e.clientY)
              overlay.style.display = ''
              onPick(e.clientX, e.clientY, target)
            }}
          >
            <div
              data-pick-ui
              className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-center gap-3 bg-brand-700/95 px-4 py-3 text-sm font-semibold text-white shadow-lg"
            >
              <Crosshair className="h-4 w-4" />
              Click the part of the page with the problem
              <button
                type="button"
                className="pointer-events-auto ml-2 bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30"
                onClick={(e) => {
                  e.stopPropagation()
                  setPicking(false)
                  setOpen(true)
                }}
              >
                Cancel
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* Highlight-the-translation banner — no full overlay, so the page
          stays selectable underneath. */}
      {highlighting &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-screenshot-exclude
            className="fixed inset-x-0 top-0 z-[100] flex select-none flex-wrap items-center justify-center gap-3 bg-brand-700/95 px-4 py-3 text-sm font-semibold text-white shadow-lg"
          >
            <Highlighter className="h-4 w-4 shrink-0" />
            <span>
              {liveSelection
                ? `Selected: “${liveSelection.length > 60 ? `${liveSelection.slice(0, 60)}…` : liveSelection}”`
                : 'Highlight the text that is translated wrong'}
            </span>
            <button
              type="button"
              disabled={!liveSelection}
              className="bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30 disabled:opacity-40"
              // preventDefault on mousedown so clicking the button doesn't
              // collapse the page selection before we read it.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setTransText(liveSelection)
                setHighlighting(false)
                setLiveSelection('')
                window.getSelection()?.removeAllRanges()
                setOpen(true)
              }}
            >
              Use highlighted text
            </button>
            <button
              type="button"
              className="bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setHighlighting(false)
                setLiveSelection('')
                setOpen(true)
              }}
            >
              Cancel
            </button>
          </div>,
          document.body
        )}

      {/* Capturing spinner */}
      {capturing &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-screenshot-exclude
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30"
          >
            <div className="flex items-center gap-2 bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-xl dark:bg-gray-900 dark:text-gray-100">
              <Loader2 className="h-4 w-4 animate-spin" /> Capturing screenshot…
            </div>
          </div>,
          document.body
        )}

      {/* Report dialog */}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-screenshot-exclude
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-4 sm:items-center"
            role="dialog"
            aria-modal="true"
            aria-label="Report an issue"
            onClick={close}
          >
            <div
              className="w-full max-w-md border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Report an issue</h2>
                  <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={pathname}>
                    {pathname}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {status === 'success' ? (
                <div className="py-2 text-center">
                  <p className="text-sm text-green-700 dark:text-green-400">{message}</p>
                  <button
                    type="button"
                    onClick={close}
                    className="mt-5 bg-brand-700 px-6 py-2 text-sm font-semibold text-white hover:bg-brand-800"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <label
                      htmlFor="report-category"
                      className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300"
                    >
                      What kind of issue?
                    </label>
                    <select
                      id="report-category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    >
                      {categories.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="report-issue"
                      className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300"
                    >
                      Describe the issue
                    </label>
                    <textarea
                      id="report-issue"
                      required={!hasTranslationHighlight}
                      rows={4}
                      value={issue}
                      onChange={(e) => setIssue(e.target.value)}
                      placeholder={
                        category === 'translation'
                          ? 'Optional — anything else we should know about this translation?'
                          : "What's wrong on this page?"
                      }
                      className="w-full resize-y border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                  </div>

                  {/* Translation report: highlight the mistranslated text */}
                  {category === 'translation' &&
                    (transText ? (
                      <div className="flex items-start gap-3 border border-gray-200 p-2 dark:border-gray-700">
                        <Highlighter className="mt-0.5 h-4 w-4 shrink-0 text-brand-700 dark:text-brand-300" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Highlighted text ({locale})
                          </p>
                          <p className="line-clamp-3 text-[11px] text-gray-500 dark:text-gray-400">
                            “{transText}”
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setTransText('')}
                          aria-label="Remove highlighted text"
                          className="shrink-0 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false)
                            setLiveSelection('')
                            setHighlighting(true)
                          }}
                          className="inline-flex items-center gap-2 border border-brand-300 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-300 dark:hover:bg-brand-900/40"
                        >
                          <Highlighter className="h-4 w-4" />
                          Highlight the mistranslated text
                        </button>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          We&apos;ll automatically include the English source text so our
                          team knows exactly what to fix.
                        </p>
                      </div>
                    ))}

                  {/* Point-at-the-issue + screenshot */}
                  {shot ? (
                    <div className="flex items-center gap-3 border border-gray-200 p-2 dark:border-gray-700">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={shot} alt="Screenshot preview" className="h-16 w-24 border border-gray-200 object-cover dark:border-gray-700" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Screenshot attached</p>
                        <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">{shotInfo}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShot(null)
                          setShotInfo('')
                        }}
                        aria-label="Remove screenshot"
                        className="shrink-0 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {shotError && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-400">
                          Couldn&apos;t capture a screenshot of this page (it may contain an
                          embedded video or protected image). You can still submit — the marked
                          location is included — or try a different spot.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false)
                          setPicking(true)
                        }}
                        className="inline-flex items-center gap-2 border border-brand-300 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-300 dark:hover:bg-brand-900/40"
                      >
                        <Crosshair className="h-4 w-4" />
                        {shotError ? 'Try again' : 'Point at the issue & attach a screenshot'}
                      </button>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This page&apos;s URL is attached automatically so we know where to look.
                  </p>
                  {status === 'error' && <p className="text-xs text-red-600">{message}</p>}
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={close}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={status === 'loading' || !canSubmit}
                      className="inline-flex items-center gap-2 bg-brand-700 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin" />}
                      {status === 'loading' ? 'Sending…' : 'Submit report'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
